/**
 * SignaturePad — canvas-based signature capture.
 *
 * Pointer-driven (works on tablet, stylus, mouse). Renders a strokes-only
 * line on a white canvas; on submit the parent reads the PNG via
 * `toBase64()` exposed via ref. Touch-action: none so the touchpad
 * doesn't pan the page while signing.
 *
 * Two affordances:
 *   - "Clear" — wipes the canvas.
 *   - `isEmpty` — visible state; parent can refuse to submit when true.
 */
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { Button } from "./Button.js";

export interface SignaturePadHandle {
  /** Read the PNG bytes as base64 (no `data:` prefix). null when empty. */
  toBase64(): string | null;
  clear(): void;
  isEmpty(): boolean;
}

interface Props {
  readonly label: string;
  /** px height of the canvas. Default 140. */
  readonly heightPx?: number;
}

export const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad(
  { label, heightPx = 140 },
  ref,
): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [, forceRender] = useState(0);

  function ctx(): CanvasRenderingContext2D | null {
    return canvasRef.current?.getContext("2d") ?? null;
  }

  function fit(): void {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const g = c.getContext("2d");
    if (!g) return;
    g.scale(dpr, dpr);
    g.fillStyle = "#ffffff";
    g.fillRect(0, 0, rect.width, rect.height);
    g.lineCap = "round";
    g.lineJoin = "round";
    g.lineWidth = 2;
    g.strokeStyle = "#0f172a";
  }

  const setupCanvas = useCallback((el: HTMLCanvasElement | null) => {
    canvasRef.current = el;
    if (el) fit();
  }, []);

  function point(ev: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = (ev.target as HTMLCanvasElement).getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  }

  function onDown(ev: React.PointerEvent<HTMLCanvasElement>): void {
    drawing.current = true;
    (ev.target as HTMLCanvasElement).setPointerCapture(ev.pointerId);
    const g = ctx();
    if (!g) return;
    const p = point(ev);
    g.beginPath();
    g.moveTo(p.x, p.y);
  }

  function onMove(ev: React.PointerEvent<HTMLCanvasElement>): void {
    if (!drawing.current) return;
    const g = ctx();
    if (!g) return;
    const p = point(ev);
    g.lineTo(p.x, p.y);
    g.stroke();
    hasInk.current = true;
  }

  function onUp(ev: React.PointerEvent<HTMLCanvasElement>): void {
    if (!drawing.current) return;
    drawing.current = false;
    try {
      (ev.target as HTMLCanvasElement).releasePointerCapture(ev.pointerId);
    } catch {
      // pointer was never captured; ignore
    }
    forceRender((n) => n + 1);
  }

  useImperativeHandle(ref, () => ({
    toBase64() {
      const c = canvasRef.current;
      if (!c || !hasInk.current) return null;
      const dataUrl = c.toDataURL("image/png");
      const comma = dataUrl.indexOf(",");
      return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    },
    clear() {
      hasInk.current = false;
      fit();
      forceRender((n) => n + 1);
    },
    isEmpty() {
      return !hasInk.current;
    },
  }));

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-medium text-slate-700">{label}</p>
        <Button
          variant="secondary"
          onClick={() => {
            hasInk.current = false;
            fit();
            forceRender((n) => n + 1);
          }}
        >
          Clear
        </Button>
      </div>
      <canvas
        ref={setupCanvas}
        style={{ height: heightPx, touchAction: "none" }}
        className="block w-full cursor-crosshair rounded-md border border-slate-300 bg-white"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      />
      {hasInk.current ? null : <p className="text-[11px] text-slate-500">Sign above to capture.</p>}
    </div>
  );
});
